<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- 헤더 -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">내 예약 관리</h1>
      <p class="mt-2 text-gray-600">예약한 좌석들을 확인하고 관리하세요</p>
    </div>

    <div class="mb-6">
      <BoardFilterPanel title="예약 상태">
        <div class="flex flex-wrap gap-2">
          <button
            @click="activeFilter = 'all'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="{
              'bg-blue-600 text-white shadow-sm': activeFilter === 'all',
              'bg-gray-100 text-gray-500 hover:text-gray-700': activeFilter !== 'all'
            }"
          >
            전체
          </button>
          <button
            @click="activeFilter = 'active'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="{
              'bg-blue-600 text-white shadow-sm': activeFilter === 'active',
              'bg-gray-100 text-gray-500 hover:text-gray-700': activeFilter !== 'active'
            }"
          >
            활성 예약
          </button>
          <button
            @click="activeFilter = 'expired'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="{
              'bg-blue-600 text-white shadow-sm': activeFilter === 'expired',
              'bg-gray-100 text-gray-500 hover:text-gray-700': activeFilter !== 'expired'
            }"
          >
            만료된 예약
          </button>
          <button
            @click="activeFilter = 'cancelled'"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            :class="{
              'bg-blue-600 text-white shadow-sm': activeFilter === 'cancelled',
              'bg-gray-100 text-gray-500 hover:text-gray-700': activeFilter !== 'cancelled'
            }"
          >
            취소된 예약
          </button>
        </div>
      </BoardFilterPanel>
    </div>

    <div class="mb-4">
      <BoardListToolbar
        v-model:pageSize="pageSize"
        :page-size-options="pageSizeOptions"
        :total-elements="totalElements"
        count-label="예약"
        :show-page-size="true"
        @page-size-change="handlePageSizeChange"
      />
    </div>

    <!-- 예약 목록 -->
    <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="n in 6" :key="n" class="bg-white rounded-lg border p-6">
        <div class="animate-pulse">
          <div class="h-4 bg-gray-300 rounded mb-2"></div>
          <div class="h-3 bg-gray-300 rounded mb-4 w-2/3"></div>
          <div class="h-3 bg-gray-300 rounded mb-2 w-1/2"></div>
          <div class="h-3 bg-gray-300 rounded w-3/4"></div>
        </div>
      </div>
    </div>

    <div v-else-if="filteredReservations?.length > 0" class="space-y-4">
      <div
        v-for="reservation in filteredReservations"
        :key="reservation.id"
        class="bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
      >
        <div class="p-6">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <!-- 예약 정보 -->
            <div class="flex-1">
              <div class="flex items-center mb-3">
                <h3 class="text-lg font-semibold text-gray-900 mr-3">
                  {{ reservation.post.title }}
                </h3>
                <span
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                  :class="getStatusBadgeClass(reservation)"
                >
                  {{ getStatusLabel(reservation) }}
                </span>
              </div>
              
              <div class="space-y-2 text-sm text-gray-600">
                <div class="flex items-center">
                  <MapPinIcon class="w-4 h-4 mr-2" />
                  <span>{{ reservation.post.place }}</span>
                </div>
                <div class="flex items-center">
                  <HomeIcon class="w-4 h-4 mr-2" />
                  <span>좌석 번호: {{ reservation.seat.seatNumber }}</span>
                </div>
                <div class="flex items-center">
                  <CalendarDaysIcon class="w-4 h-4 mr-2" />
                  <span>{{ formatDate(reservation.post.startDate) }} - {{ formatDate(reservation.post.endDate) }}</span>
                </div>
                <div v-if="getEffectiveStatus(reservation) === 'EXPIRED'" class="text-xs text-gray-500">
                  종료일시:
                  <span class="font-semibold text-gray-700">
                    {{ formatDateTime(reservation.post.endDate) }}
                  </span>
                </div>
                <div class="flex items-center">
                  <ClockIcon class="w-4 h-4 mr-2" />
                  <span>예약일: {{ formatDate(reservation.createdAt) }}</span>
                </div>
              </div>
            </div>

            <!-- 액션 버튼 -->
            <div class="mt-4 lg:mt-0 lg:ml-6 flex space-x-3">
              <RouterLink
                :to="`/posts/${reservation.post.id}`"
                class="btn btn-outline btn-sm"
              >
                상세보기
              </RouterLink>
              <button
                  v-if="getEffectiveStatus(reservation) === 'ACTIVE' && reservation.verificationToken"
                  @click="showTicketModal(reservation)"
                  class="btn btn-outline btn-sm"
              >
                티켓확인
              </button>
              <RouterLink
                v-if="getEffectiveStatus(reservation) === 'EXPIRED'"
                to="/posts"
                class="btn btn-primary btn-sm"
              >
                새 예약 찾기
              </RouterLink>
              <button
                v-if="getEffectiveStatus(reservation) === 'ACTIVE'"
                @click="showCancelModal(reservation)"
                class="btn bg-red-600 hover:bg-red-700 text-white btn-sm"
              >
                예약 취소
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 빈 상태 -->
    <div v-else class="text-center py-12">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CalendarDaysIcon class="w-8 h-8 text-gray-400" />
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">
        {{ getEmptyStateTitle() }}
      </h3>
      <p class="text-gray-600 mb-6">
        {{ getEmptyStateDescription() }}
      </p>
      <RouterLink to="/posts" class="btn btn-primary btn-sm">
        예약 가능한 장소 보기
      </RouterLink>
    </div>

    <!-- 페이지네이션 -->
    <div v-if="totalPages > 1" class="mt-8">
      <Pagination :current-page="currentPage" :total-pages="totalPages" @page-change="loadReservations" />
    </div>

    <!-- 예약 취소 확인 모달 -->
    <div v-if="selectedReservation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">예약 취소 확인</h3>
          <button @click="selectedReservation = null" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="bg-yellow-50 p-4 rounded-md">
            <ExclamationTriangleIcon class="w-5 h-5 text-yellow-400 inline mr-2" />
            <span class="text-yellow-800 font-medium">예약을 취소하시겠습니까?</span>
          </div>
          
          <div class="text-sm text-gray-600">
            <p><strong>장소:</strong> {{ selectedReservation.post.title }}</p>
            <p><strong>좌석:</strong> {{ selectedReservation.seat.seatNumber }}번</p>
            <p><strong>기간:</strong> {{ formatDate(selectedReservation.post.startDate) }} - {{ formatDate(selectedReservation.post.endDate) }}</p>
          </div>
          
          <div class="flex gap-3 pt-4">
            <button
              @click="selectedReservation = null"
              class="btn btn-outline btn-sm flex-1"
            >
              취소
            </button>
            <button
              @click="cancelReservation"
              :disabled="isCancelling"
              class="btn btn-sm bg-red-600 hover:bg-red-700 text-white flex-1"
              :class="{ 'opacity-50 cursor-not-allowed': isCancelling }"
            >
              <span v-if="isCancelling">취소 중...</span>
              <span v-else>예약 취소</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <ReservationTicketModal
        v-if="showTicket"
        :reservation="reservationData"
        @close="showTicket = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import {
  MapPinIcon,
  CalendarDaysIcon,
  ClockIcon,
  HomeIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { reservationApi } from '@/services/api'
import type { Reservation } from '@/types'
import { useToast } from 'vue-toastification'
import ReservationTicketModal from "@/components/ReservationTicketModal.vue";
import Pagination from '@/components/common/Pagination.vue'
import BoardListToolbar from '@/components/common/BoardListToolbar.vue'
import BoardFilterPanel from '@/components/common/BoardFilterPanel.vue'


const toast = useToast()

const reservations = ref<Reservation[]>([])
const isLoading = ref(true)
const isCancelling = ref(false)
const selectedReservation = ref<Reservation | null>(null)
const activeFilter = ref<'all' | 'active' | 'expired' | 'cancelled'>('active')
const reservationData = ref<Reservation | null>(null)
const showTicket = ref<boolean>(false)

// 페이지네이션
const currentPage = ref(0)
const totalPages = ref(0)
const totalElements = ref(0)
const pageSizeOptions = [6, 12, 18, 21]
const pageSize = ref(6)

// 필터링된 예약 목록
const getEffectiveStatus = (reservation: Reservation) => {
  if (reservation.effectiveStatus) {
    return reservation.effectiveStatus
  }
  if (reservation.status === 'CANCELLED') {
    return 'CANCELLED'
  }
  if (isPastReservation(reservation)) {
    return 'EXPIRED'
  }
  return 'ACTIVE'
}

const getStatusLabel = (reservation: Reservation) => {
  const status = getEffectiveStatus(reservation)
  if (status === 'ACTIVE') return '활성'
  if (status === 'EXPIRED') return '만료됨'
  return '취소됨'
}

const getStatusBadgeClass = (reservation: Reservation) => {
  const status = getEffectiveStatus(reservation)
  if (status === 'ACTIVE') return 'bg-green-100 text-green-800'
  if (status === 'EXPIRED') return 'bg-gray-100 text-gray-800'
  return 'bg-red-100 text-red-800'
}

const sortedReservations = computed(() => {
  const buckets: Record<'ACTIVE' | 'EXPIRED' | 'CANCELLED', Reservation[]> = {
    ACTIVE: [],
    EXPIRED: [],
    CANCELLED: []
  }
  reservations.value.forEach((reservation) => {
    buckets[getEffectiveStatus(reservation)].push(reservation)
  })
  return [...buckets.ACTIVE, ...buckets.EXPIRED, ...buckets.CANCELLED]
})

const filteredReservations = computed(() => {
  const source = activeFilter.value === 'all' ? sortedReservations.value : reservations.value
  if (activeFilter.value === 'all') return source
  if (activeFilter.value === 'active') {
    return source.filter((reservation) => getEffectiveStatus(reservation) === 'ACTIVE')
  }
  if (activeFilter.value === 'expired') {
    return source.filter((reservation) => getEffectiveStatus(reservation) === 'EXPIRED')
  }
  return source.filter((reservation) => getEffectiveStatus(reservation) === 'CANCELLED')
})

// 날짜 포맷
const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 과거 예약인지 확인
const isPastReservation = (reservation: Reservation) => {
  const endDate = new Date(reservation.post.endDate)
  return endDate < new Date()
}

// 빈 상태 타이틀
const getEmptyStateTitle = () => {
  switch (activeFilter.value) {
    case 'active': return '활성 예약이 없습니다'
    case 'expired': return '만료된 예약이 없습니다'
    case 'cancelled': return '취소된 예약이 없습니다'
    default: return '예약 내역이 없습니다'
  }
}

// 빈 상태 설명
const getEmptyStateDescription = () => {
  switch (activeFilter.value) {
    case 'active': return '현재 활성 상태인 예약이 없습니다.'
    case 'expired': return '기간이 지난 예약이 없습니다.'
    case 'cancelled': return '취소된 예약 내역이 없습니다.'
    default: return '아직 예약한 좌석이 없습니다. 원하는 장소를 예약해보세요.'
  }
}

// 예약 목록 로드
const loadReservations = async (page = 0) => {
  try {
    isLoading.value = true
    const response = await reservationApi.getMyReservations({
      page,
      size: pageSize.value
    })
    reservations.value = response.content
    currentPage.value = response.number
    totalPages.value = response.totalPages
    totalElements.value = response.totalElements
  } catch (error) {
    console.error('Failed to load reservations:', error)
    toast.error('예약 목록을 불러오는데 실패했습니다.')
  } finally {
    isLoading.value = false
  }
}

const handlePageSizeChange = () => {
  currentPage.value = 0
  loadReservations(0)
}

// 티켓 모달 표시
const showTicketModal = (reservation: Reservation) => {
  reservationData.value = reservation
  showTicket.value = true
}

// 취소 모달 표시
const showCancelModal = (reservation: Reservation) => {
  selectedReservation.value = reservation
}

// 예약 취소
const cancelReservation = async () => {
  if (!selectedReservation.value) return

  try {
    isCancelling.value = true
    await reservationApi.cancelReservation(selectedReservation.value.id)
    
    // 로컬 상태 업데이트
    const index = reservations.value.findIndex(r => r.id === selectedReservation.value!.id)
    if (index !== -1) {
      reservations.value[index].status = 'CANCELLED'
    }
    
    toast.success('예약이 취소되었습니다.')
    selectedReservation.value = null
  } catch (error) {
    console.error('Failed to cancel reservation:', error)
    toast.error('예약 취소에 실패했습니다.')
  } finally {
    isCancelling.value = false
  }
}

onMounted(() => {
  loadReservations()
})
</script>
